import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import './FilterPanel.css';

/**
 * @summary Filter panel component for advanced data filtering
 * @param {array} filterConfig - Array of filter configuration objects
 * @param {object} initialFilters - Initial filter values
 * @param {function} onFilterChange - Callback function when filters change
 * @returns {React.ReactElement} Returns the FilterPanel component
 */
const FilterPanel = ({ filterConfig, initialFilters = {}, onFilterChange }) => {
  const [filters, setFilters] = useState(initialFilters);
  const [isExpanded, setIsExpanded] = useState(true);
  const isInitialMount = useRef(true);
  const prevFiltersRef = useRef(JSON.stringify(initialFilters));

  // Apply filter changes
  const handleFilterChange = useCallback((key, value) => {
    // Use JSON.stringify for deep comparison to prevent infinite update loops
    const newFilters = { ...filters, [key]: value };
    
    // Only update if the filters have actually changed
    if (JSON.stringify(filters[key]) !== JSON.stringify(value)) {
      // console.log(`Filter changed: ${key} = ${JSON.stringify(value)}`);
      setFilters(newFilters);
    }
  }, [filters]);

  // Reset all filters
  const handleResetFilters = useCallback(() => {
    // console.log('Resetting all filters');
    const emptyFilters = {};
    filterConfig.forEach(filter => {
      if (filter.type === 'select' || filter.type === 'multiSelect') {
        emptyFilters[filter.key] = filter.type === 'multiSelect' ? [] : '';
      } else {
        emptyFilters[filter.key] = '';
      }
    });
    setFilters(emptyFilters);
  }, [filterConfig]);

  // Initialize filters when filterConfig changes
  useEffect(() => {
    // Only initialize if filters is empty and filterConfig has items
    if (isInitialMount.current && filterConfig.length > 0) {
      // console.log('Initializing filters from config');
      handleResetFilters();
      isInitialMount.current = false;
    }
  }, [filterConfig, handleResetFilters]);

  // Notify parent component when filters change
  useEffect(() => {
    // Skip the initial render
    if (isInitialMount.current) {
      return;
    }
    
    // Use JSON.stringify for deep comparison to prevent infinite update loops
    const filtersString = JSON.stringify(filters);
    
    if (filtersString !== prevFiltersRef.current) {
      // console.log('Notifying parent of filter changes');
      prevFiltersRef.current = filtersString;
      onFilterChange(filters);
    }
  }, [filters, onFilterChange]);

  // Toggle filter panel visibility
  const toggleFilterPanel = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="filter-panel">
      <div className="filter-panel-header">
        <h3>Advanced Filters</h3>
        <button 
          className="toggle-button" 
          onClick={toggleFilterPanel}
          aria-expanded={isExpanded}
          aria-controls="filter-panel-content"
        >
          {isExpanded ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>
      
      {isExpanded && (
        <div id="filter-panel-content" className="filter-panel-content">
          <div className="filter-grid horizontal">
            {filterConfig.map((filter) => (
              <div key={filter.key} className="filter-item">
                <label htmlFor={`filter-${filter.key}`}>{filter.label}</label>
                
                {filter.type === 'text' && (
                  <input
                    id={`filter-${filter.key}`}
                    type="text"
                    value={filters[filter.key] || ''}
                    onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                    placeholder={filter.placeholder || `Filter by ${filter.label}`}
                  />
                )}
                
                {filter.type === 'select' && (
                  <select
                    id={`filter-${filter.key}`}
                    value={filters[filter.key] || ''}
                    onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                  >
                    <option value="">All</option>
                    {filter.options && filter.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
                
                {filter.type === 'multiSelect' && (
                  <div className="multi-select-container">
                    <select
                      id={`filter-${filter.key}`}
                      multiple
                      value={filters[filter.key] || []}
                      onChange={(e) => {
                        const selectedOptions = Array.from(
                          e.target.selectedOptions,
                          (option) => option.value
                        );
                        handleFilterChange(filter.key, selectedOptions);
                      }}
                    >
                      {filter.options && filter.options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="multi-select-help">
                      Hold Ctrl (Windows) or Cmd (Mac) to select multiple options.
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            <div className="filter-actions">
              <button 
                className="reset-button" 
                onClick={handleResetFilters}
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

FilterPanel.propTypes = {
  filterConfig: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['text', 'select', 'multiSelect']).isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired
        })
      ),
      placeholder: PropTypes.string
    })
  ).isRequired,
  initialFilters: PropTypes.object,
  onFilterChange: PropTypes.func.isRequired
};

export default FilterPanel;
